import { htmlToPlainText } from './htmlToPlainText'

/**
 * Detect HowTo step data on a CMS page and return an ordered list of steps.
 *
 * Looks for HowToStepBlock entries in either bodyBlockArea or pageBlockArea.
 * Returns an empty array when no steps are found — the caller should skip
 * emitting the HowTo schema in that case (Google requires at least 2 steps
 * for valid HowTo rich results).
 *
 * Expected HowToStepBlock contract (paired with an Optimizely backend PR
 * that adds the content type):
 *
 *     contentType: ['Block', 'HowToStepBlock']
 *     title: { value: string }            // Step name (e.g. "Wash your hands")
 *     body: { value: string }              // HTML rich text — step description
 *     image?: { url, altText }             // Optional illustration
 *
 * The shape mirrors AccordionBlock's `title` + `body` convention so authors
 * have a familiar editing surface. Until the block lands in the CMS this
 * helper is dormant — it always returns an empty array and no HowTo schema
 * is emitted.
 */
export interface HowToStep {
  name: string
  text: string
  imageUrl?: string
  imageAlt?: string
}

export function extractHowToSteps(content: any): HowToStep[] {
  const blocks = [
    ...(content?.bodyBlockArea?.expandedValue ?? []),
    ...(content?.pageBlockArea?.expandedValue ?? []),
  ]
  const steps: HowToStep[] = []
  for (const block of blocks) {
    const contentTypes: string[] = block?.contentType ?? []
    if (!contentTypes.includes('HowToStepBlock'))
      continue

    const name = (block?.title?.value ?? '').trim()
    const text = htmlToPlainText(block?.body?.value)
    if (!name || !text)
      continue

    const imageUrl = block?.image?.url
    const imageAlt = block?.image?.altText
    steps.push({
      name,
      text,
      ...(typeof imageUrl === 'string' && imageUrl ? { imageUrl } : {}),
      ...(typeof imageAlt === 'string' && imageAlt ? { imageAlt } : {}),
    })
  }
  return steps
}
